import { supabase } from '../lib/supabase';
import { FandomConcept } from '../types';
import { QueryIntent } from './queryAnalyzerLLM';
import { sessionTracker } from './sessionTracker';

interface ConceptMemory {
  name: string;
  encounters: number;
  firstSeen: string;
  lastSeen: string;
  relatedConcepts: string[];
  contexts: string[]; // Queries where this concept appeared
}

interface UserMemory {
  concepts: Record<string, ConceptMemory>;
  preferences: {
    favoriteTopics: string[];
    explorationDepth: 'surface' | 'intermediate' | 'deep';
    commonQueryTypes: string[];
  };
  stats: {
    totalSearches: number;
    totalConcepts: number;
    lastActive: string;
  };
}

export class MemoryService {
  /**
   * Extract and update memory from an exploration
   */
  async updateMemoryFromExploration(
    query: string,
    concepts: FandomConcept[],
    intent: QueryIntent
  ): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get existing memory
      const { data: existingData } = await supabase
        .from('user_data')
        .select('memory')
        .eq('user_id', user.id)
        .single();

      let memory: UserMemory = existingData?.memory || {
        concepts: {},
        preferences: {
          favoriteTopics: [],
          explorationDepth: 'intermediate',
          commonQueryTypes: []
        },
        stats: {
          totalSearches: 0,
          totalConcepts: 0,
          lastActive: new Date().toISOString()
        }
      };

      // Ensure concepts object exists (fix for corrupted data)
      if (!memory.concepts || typeof memory.concepts !== 'object') {
        memory.concepts = {};
      }

      // Update concepts (guard against undefined/null concepts and filter malformed ones)
      if (!concepts || !Array.isArray(concepts)) {
        console.warn('No concepts provided to updateMemoryFromExploration, skipping concept updates');
        return;
      }
      
      // Filter out malformed concepts (missing text property)
      const validConcepts = concepts.filter(c => c && c.text && typeof c.text === 'string');
      if (validConcepts.length !== concepts.length) {
        console.warn(`Filtered out ${concepts.length - validConcepts.length} malformed concepts`);
      }
      
      const conceptNames = validConcepts.map(c => c.text);
      validConcepts.forEach(concept => {
        const conceptKey = concept.text.toLowerCase();
        
        if (memory.concepts[conceptKey]) {
          // Update existing concept
          memory.concepts[conceptKey].encounters++;
          memory.concepts[conceptKey].lastSeen = new Date().toISOString();
          memory.concepts[conceptKey].contexts.push(query);
          
          // Add related concepts
          conceptNames.forEach(related => {
            if (related !== concept.text && !memory.concepts[conceptKey].relatedConcepts.includes(related)) {
              memory.concepts[conceptKey].relatedConcepts.push(related);
            }
          });
        } else {
          // New concept
          memory.concepts[conceptKey] = {
            name: concept.text,
            encounters: 1,
            firstSeen: new Date().toISOString(),
            lastSeen: new Date().toISOString(),
            relatedConcepts: conceptNames.filter(c => c !== concept.text),
            contexts: [query]
          };
        }
      });

      // Update stats
      memory.stats.totalSearches++;
      memory.stats.totalConcepts = Object.keys(memory.concepts).length;
      memory.stats.lastActive = new Date().toISOString();

      // Update query type preferences
      if (!memory.preferences.commonQueryTypes.includes(intent.type)) {
        memory.preferences.commonQueryTypes.push(intent.type);
      }

      // Infer favorite topics (concepts with 3+ encounters)
      memory.preferences.favoriteTopics = Object.entries(memory.concepts)
        .filter(([_, data]) => data.encounters >= 3)
        .map(([_, data]) => data.name)
        .slice(0, 10);

      // Save updated memory
      await supabase
        .from('user_data')
        .upsert({
          user_id: user.id,
          memory,
          updated_at: new Date().toISOString()
        });

      console.log('Memory updated successfully');
    } catch (error) {
      console.error('Error updating memory:', error);
    }
  }

  /**
   * Get user's memory for context
   */
  async getUserMemory(): Promise<UserMemory | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_data')
        .select('memory')
        .eq('user_id', user.id)
        .single();

      if (error || !data) return null;
      return data.memory;
    } catch (error) {
      console.error('Error fetching user memory:', error);
      return null;
    }
  }

  /**
   * Get concepts related to a query based on memory
   */
  async getRelatedConcepts(query: string): Promise<string[]> {
    const memory = await this.getUserMemory();
    if (!memory) return [];

    const queryLower = query.toLowerCase();
    const related: Set<string> = new Set();

    // Find concepts that are related to the current query
    Object.entries(memory.concepts).forEach(([conceptKey, concept]) => {
      // Check if this concept name is related to the query
      if (conceptKey.includes(queryLower) || queryLower.includes(conceptKey)) {
        related.add(concept.name);
      }
      // Check if this concept was explored from a similar context
      else if (concept.contexts.some(ctx => {
        const ctxLower = ctx.toLowerCase();
        return ctxLower.includes(queryLower) || queryLower.includes(ctxLower);
      })) {
        related.add(concept.name);
      }
    });

    // Don't include the exact query itself
    related.delete(query);

    return Array.from(related).slice(0, 5);
  }

  /**
   * Track when a user clicks on a concept to explore it
   */
  async trackConceptClick(conceptText: string, parentQuery: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get existing memory
      const { data: existingData } = await supabase
        .from('user_data')
        .select('memory')
        .eq('user_id', user.id)
        .single();

      let memory: UserMemory = existingData?.memory || {
        concepts: {},
        preferences: {
          favoriteTopics: [],
          explorationDepth: 'intermediate',
          commonQueryTypes: []
        },
        stats: {
          totalSearches: 0,
          totalConcepts: 0,
          lastActive: new Date().toISOString()
        }
      };

      const conceptKey = conceptText.toLowerCase();
      
      if (memory.concepts[conceptKey]) {
        // Update existing concept
        memory.concepts[conceptKey].encounters++;
        memory.concepts[conceptKey].lastSeen = new Date().toISOString();
        if (!memory.concepts[conceptKey].contexts.includes(parentQuery)) {
          memory.concepts[conceptKey].contexts.push(parentQuery);
        }
      } else {
        // New concept
        memory.concepts[conceptKey] = {
          name: conceptText,
          encounters: 1,
          firstSeen: new Date().toISOString(),
          lastSeen: new Date().toISOString(),
          relatedConcepts: [],
          contexts: [parentQuery]
        };
      }

      // Update stats
      memory.stats.totalConcepts = Object.keys(memory.concepts).length;
      memory.stats.lastActive = new Date().toISOString();

      // Save updated memory
      await supabase
        .from('user_data')
        .upsert({
          user_id: user.id,
          memory,
          updated_at: new Date().toISOString()
        });

      console.log('Concept click tracked:', conceptText);
    } catch (error) {
      console.error('Error tracking concept click:', error);
    }
  }

  /**
   * Get user's expertise level for a topic
   */
  async getExpertiseLevel(topic: string): Promise<'beginner' | 'intermediate' | 'expert'> {
    const memory = await this.getUserMemory();
    if (!memory) return 'beginner';

    const topicLower = topic.toLowerCase();
    
    // Check if we have any concepts at all
    const totalConcepts = Object.keys(memory.concepts).length;
    if (totalConcepts === 0) return 'beginner';
    
    // Find relevant concepts
    const relevantConcepts = Object.values(memory.concepts)
      .filter(c => {
        const conceptLower = c.name.toLowerCase();
        return conceptLower.includes(topicLower) || 
               topicLower.includes(conceptLower) ||
               c.contexts.some(ctx => {
                 const ctxLower = ctx.toLowerCase();
                 return ctxLower.includes(topicLower) || topicLower.includes(ctxLower);
               });
      });

    const totalEncounters = relevantConcepts.reduce((sum, c) => sum + c.encounters, 0);

    // More generous thresholds
    if (totalEncounters >= 5) return 'expert';
    if (totalEncounters >= 2) return 'intermediate';
    if (relevantConcepts.length > 0) return 'intermediate'; // If we have any related concepts
    return 'beginner';
  }

  /**
   * Update RabbitHole stats for leaderboard
   */
  async updateRabbitHoleStats(action: 'search' | 'concept_click' | 'session_end'): Promise<void> {
    try {
      console.log('Updating RabbitHole stats for action:', action);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found, skipping stats update');
        return;
      }
      console.log('User found:', user.id, user.email);

      // Get existing data
      const { data: existingData, error: fetchError } = await supabase
        .from('user_data')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      console.log('Existing data fetch:', { existingData, fetchError });

      // Initialize both all-time and weekly stats with validation
      let stats = existingData?.rabbithole_stats || {
        total_searches: 0,
        concepts_clicked: 0,
        time_spent_minutes: 0,
        total_score: 0,
        last_session_end: null
      };

      // Ensure all stats fields are numbers (prevent null/undefined corruption)
      stats = {
        total_searches: Number(stats.total_searches) || 0,
        concepts_clicked: Number(stats.concepts_clicked) || 0,
        time_spent_minutes: Number(stats.time_spent_minutes) || 0,
        total_score: Number(stats.total_score) || 0,
        last_session_end: stats.last_session_end,
        display_name: stats.display_name
      };

      let weeklyStats = existingData?.weekly_stats || {
        total_searches: 0,
        concepts_clicked: 0,
        time_spent_minutes: 0,
        total_score: 0,
        week_start: null,
        last_session_end: null
      };

      // Ensure all weekly stats fields are numbers
      weeklyStats = {
        total_searches: Number(weeklyStats.total_searches) || 0,
        concepts_clicked: Number(weeklyStats.concepts_clicked) || 0,
        time_spent_minutes: Number(weeklyStats.time_spent_minutes) || 0,
        total_score: Number(weeklyStats.total_score) || 0,
        week_start: weeklyStats.week_start,
        last_session_end: weeklyStats.last_session_end,
        display_name: weeklyStats.display_name
      };

      // Update based on action - ADD points incrementally instead of recalculating
      switch (action) {
        case 'search':
          stats.total_searches = (stats.total_searches || 0) + 1;
          stats.total_score = (stats.total_score || 0) + 10; // Add 10 points for search
          weeklyStats.total_searches = (weeklyStats.total_searches || 0) + 1;
          weeklyStats.total_score = (weeklyStats.total_score || 0) + 10; // Add 10 points for search
          sessionTracker.trackActivity();
          break;
        case 'concept_click':
          stats.concepts_clicked = (stats.concepts_clicked || 0) + 1;
          stats.total_score = (stats.total_score || 0) + 20; // Add 20 points for concept
          weeklyStats.concepts_clicked = (weeklyStats.concepts_clicked || 0) + 1;
          weeklyStats.total_score = (weeklyStats.total_score || 0) + 20; // Add 20 points for concept
          sessionTracker.trackActivity();
          break;
        case 'session_end':
          const sessionMinutes = sessionTracker.endSession();
          stats.time_spent_minutes = (stats.time_spent_minutes || 0) + sessionMinutes;
          stats.total_score = (stats.total_score || 0) + (sessionMinutes * 5); // Add 5 points per minute
          weeklyStats.time_spent_minutes = (weeklyStats.time_spent_minutes || 0) + sessionMinutes;
          weeklyStats.total_score = (weeklyStats.total_score || 0) + (sessionMinutes * 5); // Add 5 points per minute
          stats.last_session_end = new Date().toISOString();
          weeklyStats.last_session_end = new Date().toISOString();
          break;
      }

      // Save updated stats - ensure all fields are present
      const displayName = user.user_metadata?.full_name || 
                         user.user_metadata?.name || 
                         user.email?.split('@')[0] || 
                         'Explorer';
      
      // Add display name to stats for leaderboard
      stats.display_name = displayName;
      weeklyStats.display_name = displayName;
      
      const { error: upsertError } = await supabase
        .from('user_data')
        .upsert({
          user_id: user.id,
          rabbithole_stats: stats,
          weekly_stats: weeklyStats,
          memory: existingData?.memory || {},
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });
      
      if (upsertError) {
        console.error('Failed to upsert rabbithole_stats:', upsertError);
      }

      console.log('RabbitHole stats updated:', stats);
      console.log('Weekly stats updated:', weeklyStats);
      console.log('Upsert result:', { error: upsertError });
    } catch (error) {
      console.error('Error updating RabbitHole stats:', error);
    }
  }
}

export const memoryService = new MemoryService();